import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";
import { articles } from "@/mocks/articles";

function formatToday() {
  const now = new Date();
  const week = ["日", "一", "二", "三", "四", "五", "六"][now.getDay()];
  return `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日 星期${week}`;
}

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSearchOpen(false);
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return articles
      .filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          a.topicName.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q))
      )
      .slice(0, 6);
  }, [query]);

  const goToResult = (slug: string) => {
    setQuery("");
    setSearchOpen(false);
    navigate(`/article/${slug}`);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap px-3 py-2 text-sm font-label font-medium transition-colors cursor-pointer ${
      isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="bg-secondary-900 text-background-100">
        <div className="flex h-9 items-center justify-between px-4 md:px-6 text-[11px] md:text-xs font-label">
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-background-200">{formatToday()}</span>
            <span className="inline-flex items-center gap-1.5 text-accent-300">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
              </span>
              首发追踪进行中
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href={`mailto:${siteInfo.tipEmail}`} className="hidden md:inline cursor-pointer text-background-200 hover:text-background-50 transition-colors">
              <i className="ri-megaphone-line mr-1" aria-hidden="true" />
              爆料投稿
            </a>
            <span className="text-background-300">|</span>
            <span className="text-background-200">每日持续更新</span>
          </div>
        </div>
      </div>

      <div
        className={`w-full border-b transition-colors duration-300 ${
          scrolled ? "border-background-200 bg-background-50/95 backdrop-blur-md" : "border-transparent bg-background-50"
        }`}
      >
        <div className="flex h-16 items-center gap-4 px-4 md:px-6">
          <Link to="/" className="group flex flex-shrink-0 items-center gap-2.5 cursor-pointer" aria-label="独家大瓜首页">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 font-heading text-xl font-black text-background-50">
              瓜
            </span>
            <span className="leading-none">
              <span className="block font-heading text-lg font-black tracking-tight text-foreground-950">
                {siteInfo.name}
              </span>
              <span className="mt-0.5 block text-[10px] font-label tracking-[0.22em] text-foreground-400">
                FIRST SCOOP
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex lg:items-center lg:gap-0.5" aria-label="主导航">
            {navLinks.map((link) => (
              <NavLink key={link.to} to={link.to} className={navLinkClass} end={link.to === "/"}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative hidden sm:block">
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                placeholder="搜索爆料关键词"
                aria-label="搜索爆料"
                className="h-9 w-40 rounded-full border border-background-300 bg-background-100 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-all focus:w-52 focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-200"
              />
              <i
                className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
                aria-hidden="true"
              />
              {searchOpen && query.trim() && (
                <div className="absolute right-0 top-11 w-80 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  {results.length > 0 ? (
                    <ul className="max-h-80 overflow-y-auto py-1">
                      {results.map((r) => (
                        <li key={r.slug}>
                          <button
                            type="button"
                            onClick={() => goToResult(r.slug)}
                            className="flex w-full cursor-pointer flex-col gap-1 px-3 py-2.5 text-left transition-colors hover:bg-primary-50"
                          >
                            <span className="text-sm font-medium text-foreground-900 line-clamp-1">{r.title}</span>
                            <span className="text-[11px] text-foreground-500">
                              {r.topicName} · {r.reliability}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="px-3 py-4 text-sm text-foreground-500">没有找到相关爆料，换个关键词试试</p>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/about#contact"
              className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-label font-bold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer md:inline-flex"
            >
              订阅爆料
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden">
          <div
            className="fixed inset-0 top-[100px] bg-foreground-950/40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <nav className="relative border-b border-background-200 bg-background-50 px-4 py-4" aria-label="移动端导航">
            <div className="relative mb-3 sm:hidden">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索爆料关键词"
                aria-label="搜索爆料"
                className="h-10 w-full rounded-full border border-background-300 bg-background-100 pl-9 pr-3 text-sm outline-none focus:border-primary-400"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" aria-hidden="true" />
              {query.trim() && (
                <ul className="mt-2 overflow-hidden rounded-lg border border-background-200">
                  {results.map((r) => (
                    <li key={r.slug}>
                      <button
                        type="button"
                        onClick={() => goToResult(r.slug)}
                        className="w-full cursor-pointer px-3 py-2 text-left text-sm text-foreground-800 hover:bg-primary-50"
                      >
                        {r.title}
                      </button>
                    </li>
                  ))}
                  {results.length === 0 && <li className="px-3 py-3 text-sm text-foreground-500">没有找到相关爆料</li>}
                </ul>
              )}
            </div>
            <ul className="flex flex-col">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.to === "/"}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `block cursor-pointer rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                        isActive ? "bg-primary-50 text-primary-600" : "text-foreground-800 hover:bg-background-100"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
            <Link
              to="/about#contact"
              onClick={() => setMenuOpen(false)}
              className="mt-3 flex w-full items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-label font-bold text-background-50 cursor-pointer"
            >
              订阅爆料
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}